import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { saveTerraUserId, fetchTerraUsersByReferenceId } from "../services/terraService";

const ConnectionResult: React.FC = () => {
  const location = useLocation();
  const { currentUser, authState } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<{
    success: boolean;
    resource?: string;
    saving?: boolean;
    error?: string;
  } | null>(null);
  const [showFallbackMessage, setShowFallbackMessage] = useState(false);

  // Determine success/failure from pathname
  const isSuccessPath = location.pathname === "/connectionsuccess";
  const isFailurePath = location.pathname === "/connectionfailed";

  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(location.search);
      const userId = urlParams.get("user_id");
      const resource = urlParams.get("resource");
      const referenceId = urlParams.get("reference_id");

      // Check if we're on a valid path
      if (!isSuccessPath && !isFailurePath) {
        // Unknown path - show simple message
        setConnectionStatus({
          success: false,
          error: "NO_PARAMS",
          saving: false,
        });
        return;
      }

      // If on failure path, set status immediately
      if (isFailurePath) {
        setConnectionStatus({
          success: false,
          resource: resource || undefined,
          error: "Device connection was cancelled or failed. Please try again.",
          saving: false,
        });

        // Notify parent window
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: "TERRA_CONNECTION_RESULT",
              success: false,
              resource: resource || undefined,
            },
            window.location.origin
          );
        }
        return;
      }
      // ─── GARMIN-ONLY BRANCH ───────────────────────────────────────────────
      // Garmin's OAuth is async — it does NOT include user_id in the redirect
      // URL. We poll the Terra API until Garmin confirms, then save to DB.
      // All other devices go through the original flow below, untouched.
      if (resource === "GARMIN" && !userId && currentUser?.uid) {
        console.log("Garmin detected without user_id — starting Terra poll...");

        setConnectionStatus({
          success: true,
          resource: "GARMIN",
          saving: true,
        });

        const MAX_ATTEMPTS = 10;
        const POLL_INTERVAL_MS = 3000; // 3 seconds between attempts
        let attempts = 0;

        const pollForGarminUser = async (): Promise<void> => {
          try {
            const terraUsers = await fetchTerraUsersByReferenceId();
            const garminUser = terraUsers.find(
              (u) => u.provider === "GARMIN" && u.active
            );

            if (garminUser) {
              // Found — save to DB exactly like other devices do
              console.log("Garmin user found via poll:", garminUser.user_id);
              await saveTerraUserId(garminUser.user_id, "GARMIN");

              // Track with Meta Pixel if available
              if (window.fbq) {
                window.fbq("trackCustom", "WearableConnected", {
                  device_type: "GARMIN",
                });
              }

              setConnectionStatus({
                success: true,
                resource: "GARMIN",
                saving: false,
              });

              // Notify parent window
              if (window.opener && !window.opener.closed) {
                window.opener.postMessage(
                  {
                    type: "TERRA_CONNECTION_RESULT",
                    success: true,
                    resource: "GARMIN",
                  },
                  window.location.origin
                );
              }
            } else if (attempts < MAX_ATTEMPTS) {
              attempts++;
              console.log(`Garmin poll attempt ${attempts}/${MAX_ATTEMPTS} — not found yet, retrying...`);
              await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
              await pollForGarminUser();
            } else {
              // Timed out after ~30s — show success UI anyway so user isn't
              // stuck. The device shows in Terra dashboard; they can refresh.
              console.warn("Garmin poll timed out — showing success without DB save");
              setConnectionStatus({
                success: true,
                resource: "GARMIN",
                saving: false,
              });

              if (window.opener && !window.opener.closed) {
                window.opener.postMessage(
                  {
                    type: "TERRA_CONNECTION_RESULT",
                    success: true,
                    resource: "GARMIN",
                  },
                  window.location.origin
                );
              }
            }
          } catch (error) {
            console.error("Error during Garmin poll:", error);
            // On poll error, show success anyway — better UX than showing failure
            setConnectionStatus({
              success: true,
              resource: "GARMIN",
              saving: false,
            });

            if (window.opener && !window.opener.closed) {
              window.opener.postMessage(
                {
                  type: "TERRA_CONNECTION_RESULT",
                  success: true,
                  resource: "GARMIN",
                },
                window.location.origin
              );
            }
          }
        };

        await pollForGarminUser();
        return;
      }
      // ─── END GARMIN-ONLY BRANCH ───────────────────────────────────────────

      // Success case - show success regardless of auth status
      // Only try to save if we have all necessary data AND authenticated user
      const canSaveDevice = userId && resource && currentUser?.uid;

      if (!canSaveDevice) {
        // Missing data or not authenticated - webhook will handle it, but show success
        setConnectionStatus({
          success: true,
          resource: resource || "device",
          saving: false,
          error: undefined,
        });

        // Notify parent window
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: "TERRA_CONNECTION_RESULT",
              success: true,
              resource: resource || undefined,
            },
            window.location.origin
          );
        }
        return;
      }

      // We have all data and authenticated user - try to save
      setConnectionStatus({
        success: true,
        resource: resource,
        saving: true,
      });

      // Save the device to the database
      try {
        // Verify reference_id matches current user
        if (referenceId && referenceId !== currentUser?.uid) {
          throw new Error(
            "Reference ID mismatch. Please try connecting again."
          );
        }

        // Save Terra device ID
        await saveTerraUserId(userId, resource);

        console.log("Terra device connected successfully:", {
          userId,
          resource,
        });

        // Track wearable connection with Meta Pixel
        if (window.fbq) {
          window.fbq("trackCustom", "WearableConnected", {
            device_type: resource,
          });
        }

        setConnectionStatus({
          success: true,
          resource: resource,
          saving: false,
        });

        // Notify parent window if this is a popup
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: "TERRA_CONNECTION_RESULT",
              success: true,
              resource: resource,
            },
            window.location.origin
          );
        }
      } catch (error) {
        console.error("Error saving Terra device:", error);

        // Show success anyway - webhook will handle it
        setConnectionStatus({
          success: true,
          resource: resource,
          saving: false,
          error: undefined,
        });

        // Notify parent window of success (webhook will handle the save)
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: "TERRA_CONNECTION_RESULT",
              success: true,
              resource: resource,
            },
            window.location.origin
          );
        }
      }
    };

    handleCallback();
  }, [
    location.pathname,
    location.search,
    currentUser?.uid,
    authState.loading,
    authState.isAuthenticated,
    isSuccessPath,
    isFailurePath,
  ]);

  const handleClose = () => {
    window.close();

    // Check if window actually closed after a short delay
    setTimeout(() => {
      // If we're still here, the window didn't close
      // (window.close() only works for windows opened by scripts)
      setShowFallbackMessage(true);
    }, 100);
  };

  if (!connectionStatus || connectionStatus.saving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg mb-4"></div>
          <p className="text-base-content/70">
            {connectionStatus?.saving
              ? "Saving device connection..."
              : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  // Show simple message if no URL parameters
  if (connectionStatus.error === "NO_PARAMS") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
        <div className="text-center">
          <p className="text-2xl font-semibold text-base-content">
            Go back to the PEAK^ app
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
      <div className="max-w-md p-8 ">
        {connectionStatus.success && !connectionStatus.error ? (
          <>
            <div className=" text-center">
              {/* Success Icon */}
              <div className="flex justify-center">
                <svg
                  className="w-10 h-10 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              <h1 className="text-4xl font-bold font-grotesk text-base-contentp py-4 pb-2">
                Success
              </h1>
            </div>
            <p className="text-base-content/70 mb-6 px-10 text-center">
              {connectionStatus.resource
                ? `Your ${connectionStatus.resource} in now connected.`
                : "Your device is now connected."}
            </p>
          </>
        ) : (
          <>
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-10 h-10 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-bold text-base-content mb-2">
              Connection Failed
            </h1>
            <p className="text-base-content/70 mb-6">
              {connectionStatus.error ||
                "We couldn't connect your device. Please try again or contact support if the problem persists."}
            </p>
          </>
        )}

        {connectionStatus.resource === "APPLE" ? (
          <div className="flex items-center gap-4 p-4 bg-base-200 rounded-lg border border-base-300">
            <img
              src="/logo192.png"
              alt="PEAK"
              className="flex-shrink-0 w-12 h-12 rounded-xl"
            />
            <p className="text-blue-800 dark:text-blue-300  text-left leading-[1.15]">
              Go back to <strong>PEAK^</strong> and close the connection
              tabs
            </p>
          </div>
        ) : showFallbackMessage ? (
          <div className="flex items-center gap-4 p-4 bg-base-200 rounded-lg border border-base-300">
            <img
              src="/logo192.png"
              alt="PEAK"
              className="flex-shrink-0 w-12 h-12 rounded-xl"
            />
            <p className="text-blue-800 dark:text-blue-300  text-left leading-[1.15]">
              Go back to <strong>PEAK^</strong> to finish connection
            </p>
          </div>
        ) : (
          <button
            onClick={handleClose}
            className="w-full bg-neutral text-neutral-content rounded-lg py-3 px-6 font-semibold hover:bg-neutral/90 transition-colors"
          >
            Okay, next step.
          </button>
        )}
      </div>
    </div>
  );
};

export default ConnectionResult;
